import dayjs, { type Dayjs } from "dayjs";
import duration, { type Duration } from "dayjs/plugin/duration";
import type { ValueErrorsEnum } from "kubb";
import type { ColumnMap } from "../../../model/deviceTimeSeries";
dayjs.extend(duration);
export interface ErrorBand {
  start: Dayjs;
  end: Dayjs;
  errors: { [key: string]: ValueErrorsEnum[] };
}

export const identifyErrorBands = (
  data: ColumnMap,
  times: Dayjs[],
  errorBandDuration?: Duration,
): Array<ErrorBand> => {
  /*
              Maps a set of ColumnMap data and matching times array to an array of ErrorBands.
  
              Each ErrorBand will have matching start and end time, overlaps are not taken into account.
               */

  const timeAdjust: Duration =
    errorBandDuration !== undefined
      ? dayjs.duration(errorBandDuration.asMinutes() / 2, "minute")
      : dayjs.duration(0, "seconds");

  return times
    .flatMap((time, time_index): ErrorBand | undefined => {
      let errorCount = 0;
      const errors: { [key: string]: Array<ValueErrorsEnum> } = Object.entries(
        data,
      ).reduce((e, [key, v]) => {
        const thisErrors =
          v.errors?.[time_index].filter((v) => v !== null && v !== undefined) ||
          [];
        const thisErrorCount = thisErrors.length || 0;

        if (thisErrorCount !== 0) {
          errorCount += thisErrorCount;
          return {
            ...e,
            [key]: thisErrors,
          };
        } else {
          return e;
        }
      }, {});
      if (errorCount === 0) {
        return undefined;
      } else {
        const start = time.clone().subtract(timeAdjust);
        const end = dayjs(time.clone().add(timeAdjust));

        return { start, end, errors };
      }
    })
    .filter((v) => {
      return v !== undefined;
    }) as Array<ErrorBand>;
};

export const removeValueErrorsFromData = (data: ColumnMap): ColumnMap => {
  return Object.entries(data).reduce(
    (p, [k, v]) => ({
      ...p,
      [k]: { ...v, data: removeErrorsFromDataArray(v.data, v.errors) },
    }),
    {},
  );
};
const removeErrorsFromDataArray = (
  data: (string | undefined)[],
  errors?: ValueErrorsEnum[][],
): Array<string | undefined | null> => {
  if (errors === undefined || errors.length !== data.length) {
    return data;
  } else {
    return data.flatMap((value, index) => {
      const thisErrors = errors[index].filter((v) => v !== null);
      return thisErrors.length === 0 ? value : null;
    });
  }
};

export const identifyBestDurationForErrorBands = (times: Dayjs[]): Duration => {
  if (times.length > 1) {
    const firstTime = times[0];
    const lastTime = times[times.length - 1];
    const diff = lastTime.diff(firstTime, "hours");
    switch (true) {
      case diff <= 23:
        return dayjs.duration(30, "minute");
      case diff <= 167:
        return dayjs.duration(2, "hours");
      case diff <= 671:
        return dayjs.duration(6, "hours");
      default:
        return dayjs.duration(24, "hours");
    }
  }
  return dayjs.duration(2, "hours");
};
